import {
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useAuth } from "@/features/auth";
import { v4 as uuidv4 } from "uuid";
import { getStoredUserId, setStoredUserId, getStoredUserName, setStoredUserName } from "./user-storage";
import { UserContext } from "./user-context";

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const { isAuthEnabled, firebaseUser, isAuthReady } = useAuth();

  const [userId, setUserId] = useState<string>("");
  const [userName, setUserNameState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Wait for auth to be fully ready (including App Check) if auth is enabled
    // This prevents race conditions where we try to access Firestore before App Check is initialized
    if (isAuthEnabled && !isAuthReady) {
      return;
    }

    const init = async () => {
      // If auth is enabled and we have a Firebase user, use their UID
      if (isAuthEnabled && firebaseUser) {
        const firebaseUid = firebaseUser.uid;
        const firebaseEmail = firebaseUser.email;
        const firebaseDisplayName = firebaseUser.displayName;

        // Store Firebase UID as user ID for consistency with backend
        setStoredUserId(firebaseUid);
        setUserId(firebaseUid);

        // Use Firebase display name or email as fallback for user name
        const name = firebaseDisplayName || firebaseEmail?.split("@")[0] || null;
        if (name) {
          setStoredUserName(name);
          setUserNameState(name);
        }

        setIsLoading(false);
        return;
      }

      // Non-auth flow: use stored or generate new user ID
      let storedUserId = getStoredUserId();
      if (!storedUserId) {
        storedUserId = uuidv4();
        setStoredUserId(storedUserId);
      }

      setUserId(storedUserId);
      setUserNameState(getStoredUserName());
      setIsLoading(false);
    };

    init();
  }, [isAuthEnabled, firebaseUser, isAuthReady]);

  const setUserName = (name: string) => {
    setUserNameState(name);
    setStoredUserName(name);
  };

  return (
    <UserContext.Provider value={{ userId, userName, setUserName, isLoading }}>
      {children}
    </UserContext.Provider>
  );
}
